import { useMemo } from "react";
import { Line } from "@react-three/drei";
import { CubicBezierCurve3, CurvePath, Vector3 } from "three";
import { INK, RO, Z } from "../theme3d";
import { usePanelOccluded } from "./PanelOcclusionContext";

// The selection ring's reference path, transcribed from
// design/Diegetic Panel System.dc.html's brush-stroke SVG (viewBox 0 0 230
// 70): "M14 40 C 14 12, 60 6, 115 6 C 170 6, 216 16, 216 38 C 216 60, 168
// 64, 115 64" — an open ~330° ring with the gap at bottom-left.
const VB_W = 230;
const VB_H = 70;

function toLocal(px: number, py: number): [number, number] {
  // SVG is y-down; three.js/world-space is y-up.
  return [px / VB_W - 0.5, -(py / VB_H - 0.5)];
}

function ensoPoints(width: number, height: number, segments = 96): Vector3[] {
  const toV3 = (px: number, py: number) => {
    const [lx, ly] = toLocal(px, py);
    return new Vector3(lx * width, ly * height, 0);
  };

  const path = new CurvePath<Vector3>();
  path.add(new CubicBezierCurve3(toV3(14, 40), toV3(14, 12), toV3(60, 6), toV3(115, 6)));
  path.add(new CubicBezierCurve3(toV3(115, 6), toV3(170, 6), toV3(216, 16), toV3(216, 38)));
  path.add(new CubicBezierCurve3(toV3(216, 38), toV3(216, 60), toV3(168, 64), toV3(115, 64)));

  return path.getPoints(segments);
}

export interface EnsoProps {
  width: number;
  height: number;
  color?: string;
  opacity?: number;
  lineWidth?: number;
  position?: [number, number, number];
  occluded?: boolean;
}

// The gold selection ring — replaces the old translucent-box highlight
// (the "looks like a bug" defect the HANDOFF calls out in §4c). <Line>
// with worldUnits over a CanvasTexture/TubeGeometry: native round caps/
// joins (the mockup's stroke-linecap:round) and, critically, `worldUnits`
// makes the stroke thickness independent of `resolution` — the drei Line
// feeds `resolution` from the mirror canvas, which doesn't match the XR
// framebuffer's per-eye size.
export function Enso({
  width,
  height,
  color = INK.title,
  opacity = 0.92,
  lineWidth = 0.012,
  position = [0, 0, 0],
  occluded,
}: EnsoProps) {
  const resolvedOccluded = usePanelOccluded(occluded);
  const points = useMemo(() => ensoPoints(width, height), [width, height]);
  const [x, y, z] = position;

  return (
    <Line
      points={points}
      color={color}
      lineWidth={lineWidth}
      worldUnits
      transparent
      opacity={opacity}
      depthTest={resolvedOccluded}
      depthWrite={false}
      position={[x, y, z + Z.enso]}
      renderOrder={resolvedOccluded ? RO.panelEnso : RO.overlayBacking + 1}
      toneMapped={false}
    />
  );
}
